import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * VerifyLead — public /verify-lead/:token page.
 *
 * Owner clicks the link in the confirmation email we send after they submit
 * the /provider handoff form. On success we mark the owner_lead as verified.
 * This is the "double opt-in" gate — until this fires, the lead is pending.
 */
export default function VerifyLead() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, ok: false, already: false, email: "", reason: "" });

  const fired = useRef("");
  useEffect(() => {
    if (fired.current === token) return;
    fired.current = token;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/owner-leads/verify/${encodeURIComponent(token)}`);
        setState({
          loading: false, ok: !!r.data.ok, already: !!r.data.already_verified,
          email: r.data.email || "", reason: r.data.reason || "",
        });
      } catch {
        setState({ loading: false, ok: false, already: false, email: "", reason: "error" });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="verify-lead-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3"><Link to="/"><Brand variant="dark" size={18} /></Link></div>
      </header>
      <main className="max-w-md mx-auto px-4 py-12">
        {state.loading && <div className="text-sm text-slate-500" data-testid="verify-loading">Confirming…</div>}
        {!state.loading && state.ok && (
          <div className="p-6 rounded-lg border border-emerald-300 bg-emerald-50" data-testid="verify-ok">
            <CheckCircle2 className="w-6 h-6 text-emerald-700 mb-3" />
            <h1 className="text-2xl font-bold text-emerald-900 tracking-[-0.01em]">
              {state.already ? "You’re already confirmed" : "Email confirmed"}
            </h1>
            <p className="text-sm text-emerald-900 mt-2 leading-relaxed">
              {state.already
                ? "Thanks — you’ve already confirmed this email. A person will review your property and reply within two business days."
                : "Thanks — your email is confirmed. A person will review your property and reply within two business days."}
            </p>
            <p className="text-xs text-emerald-800 mt-3 leading-relaxed">Your property has not been listed or published anywhere.</p>
          </div>
        )}
        {!state.loading && !state.ok && (
          <div className="p-6 rounded-lg border border-amber-300 bg-amber-50" data-testid="verify-bad">
            <AlertTriangle className="w-6 h-6 text-amber-700 mb-3" />
            <h1 className="text-lg font-bold text-amber-900">This link is no longer valid</h1>
            <p className="text-sm text-amber-900 mt-2 leading-relaxed">
              {state.reason === "expired"
                ? "This confirmation link has expired. If you still want to hear back from Throughline, submit your property again from the owner page."
                : "We couldn’t find this confirmation. It may have already been used, or the link may be incomplete."}
            </p>
            <Link to="/provider" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 underline" data-testid="verify-go-provider">
              Go to the owner page
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
